(() => {
    if (customElements.get('po-header-menu-collection')) return;

    const LOG_PREFIX = '[po-header-menu-collection]';
    const CARDS_CACHE_KEY = 'cards';
    const COLLECTIONS_CACHE_KEY = 'headerMenuCollections';

    const ensureAlgoliaCache = () => {
        if (!window.poAlgoliaCache) {
            const cards = new Map();
            window.poAlgoliaCache = {
                cards,
                clearCards: () => cards.clear(),
            };
        }

        if (!(window.poAlgoliaCache[CARDS_CACHE_KEY] instanceof Map)) {
            window.poAlgoliaCache[CARDS_CACHE_KEY] = new Map();
        }

        if (!(window.poAlgoliaCache[COLLECTIONS_CACHE_KEY] instanceof Map)) {
            window.poAlgoliaCache[COLLECTIONS_CACHE_KEY] = new Map();
        }

        return window.poAlgoliaCache;
    };

    class PoHeaderMenuCollection extends HTMLElement {
        connectedCallback() {
            this._aborted = false;
            this.productsSource = (this.dataset.source || 'algolia').trim();
            this.collectionHandle = (this.dataset.collectionHandle || '').trim();
            this.ruleContext = (this.dataset.ruleContext || '').trim();
            this.useRuleContextOnly = this.dataset.useRuleContextOnly === 'true';
            this.limit = Math.max(1, parseInt(this.dataset.limit || '1', 10));
            this.cardView = this.dataset.cardView || 'po-menu-product-card';
            this.cardSelector = this.dataset.cardSelector || '#po-menu-product-card';
            this.showOnlyAvailable = this.dataset.showOnlyAvailable !== 'false';
            this.swiperWrapper = this.querySelector('[data-ddm-products-wrapper]');
            this.swiperPagination = this.querySelector('[data-ddm-pagination]');
            this.swiperRoot = this.querySelector('swiper-slider');
            this.fallbackTemplate = this.querySelector('[data-ddm-fallback-template]');

            if (!this.swiperWrapper || this.dataset.poDdmObserved === 'true') {
                return;
            }

            this.togglePagination(this.swiperWrapper.children.length);

            if (this.productsSource !== 'algolia') {
                this.dataset.poDdmLoaded = 'true';
                return;
            }

            if (!this.canLoadFromAlgolia()) {
                return;
            }

            this.observeWhenVisible();
        }

        disconnectedCallback() {
            this._aborted = true;
            this._observer?.disconnect();
        }

        observeWhenVisible() {
            if (typeof IntersectionObserver !== 'function') {
                this.loadProducts();
                return;
            }

            this.dataset.poDdmObserved = 'true';
            this._observer = new IntersectionObserver((entries, observer) => {
                if (!entries.some((entry) => entry.isIntersecting)) return;

                observer.disconnect();
                this.loadProducts();
            }, {threshold: 0.15});

            this._observer.observe(this);
        }

        canLoadFromAlgolia() {
            return Boolean(this.collectionHandle || this.ruleContext);
        }

        shouldUseRuleContextOnly() {
            return this.useRuleContextOnly && Boolean(this.ruleContext);
        }

        getRuleContextFallbackIndexName() {
            const algoliaUtils = window.poAlgoliaUtils;
            const indexPrefix = algoliaUtils?.getConfig?.()?.index_prefix;
            const baseIndexName =
                algoliaUtils?.buildProductsIndexName?.({indexPrefix})
                || algoliaUtils?.DEFAULT_INDEX
                || 'shopify_products';
            const currentIndexName =
                algoliaUtils?.getLastIndexName?.(null)
                || algoliaUtils?.getDefaultProductsIndex?.()
                || '';

            if (!baseIndexName || !currentIndexName || baseIndexName === currentIndexName) {
                return '';
            }

            return baseIndexName;
        }

        async loadProducts() {
            if (
                this._aborted
                || this.productsSource !== 'algolia'
                || this.dataset.poDdmLoaded === 'true'
                || this.dataset.poDdmLoading === 'true'
                || !this.canLoadFromAlgolia()
            ) {
                return;
            }

            this.dataset.poDdmLoading = 'true';

            try {
                const handles = await this.fetchCollectionHandles();

                if (!handles.length) {
                    this.restoreFallbackSlides({clearIfMissing: true});
                    this.dataset.poDdmLoaded = 'true';
                    return;
                }

                const cardResults = await Promise.all(
                    handles.map(async (handle) => ({
                        handle,
                        html: await this.fetchProductCardHTML(handle),
                    }))
                );

                const slides = cardResults.flatMap(({html}) => (html ? [this.buildSlide(html)] : []));

                if (!slides.length) {
                    this.restoreFallbackSlides({clearIfMissing: true});
                    this.dataset.poDdmLoaded = 'true';
                    return;
                }

                const fragment = document.createDocumentFragment();
                slides.forEach((slide) => fragment.appendChild(slide));

                this.swiperWrapper.innerHTML = '';
                this.swiperWrapper.appendChild(fragment);
                this.togglePagination(slides.length);

                requestAnimationFrame(() => {
                    this.swiperRoot?.refreshSwiper?.();
                });

                this.dataset.poDdmLoaded = 'true';
            } catch (error) {
                console.error(LOG_PREFIX, 'loadProducts error', error);
                this.restoreFallbackSlides({clearIfMissing: true});
            } finally {
                this.dataset.poDdmLoading = 'false';
            }
        }

        fetchCollectionHandles() {
            const helper = window.poAlgoliaFetchCollectionHandles;
            const cache = ensureAlgoliaCache()[COLLECTIONS_CACHE_KEY];
            const useRuleContextOnly = this.shouldUseRuleContextOnly();
            const resolvedCollectionHandle = useRuleContextOnly ? '' : this.collectionHandle;
            const cacheKey = `${resolvedCollectionHandle}|${this.limit}|${this.showOnlyAvailable}|${this.ruleContext}|${useRuleContextOnly}`;

            if (cache.has(cacheKey)) {
                return cache.get(cacheKey);
            }

            if (typeof helper !== 'function') {
                console.error(LOG_PREFIX, 'Helper poAlgoliaFetchCollectionHandles mancante');
                return Promise.resolve([]);
            }

            const pending = helper({
                collectionHandle: resolvedCollectionHandle,
                limit: this.limit,
                showOnlyAvailable: this.showOnlyAvailable,
                ruleContext: this.ruleContext,
                includeCollectionHandleInRuleContexts: !useRuleContextOnly,
                logPrefix: LOG_PREFIX,
                errorLabel: 'fetchCollectionHandles error',
            }).then(async (handles) => {
                if (!handles.length && useRuleContextOnly) {
                    const fallbackIndexName = this.getRuleContextFallbackIndexName();

                    if (fallbackIndexName) {
                        handles = await helper({
                            collectionHandle: resolvedCollectionHandle,
                            limit: this.limit,
                            indexName: fallbackIndexName,
                            showOnlyAvailable: this.showOnlyAvailable,
                            ruleContext: this.ruleContext,
                            includeCollectionHandleInRuleContexts: !useRuleContextOnly,
                            logPrefix: LOG_PREFIX,
                            debugLabel: `Algolia handles fallback (${fallbackIndexName}):`,
                            errorLabel: `fetchCollectionHandles fallback error (${fallbackIndexName})`,
                        });
                    }
                }

                const safeHandles = Array.isArray(handles) ? handles.filter(Boolean).slice(0, this.limit) : [];
                cache.set(cacheKey, Promise.resolve(safeHandles));
                return safeHandles;
            }).catch((error) => {
                cache.delete(cacheKey);
                throw error;
            });

            cache.set(cacheKey, pending);
            return pending;
        }

        fetchProductCardHTML(handle) {
            const cache = ensureAlgoliaCache()[CARDS_CACHE_KEY];
            const cacheKey = `${handle}|${this.cardView}`;

            if (cache.has(cacheKey)) {
                return cache.get(cacheKey);
            }

            const pending = (async () => {
                const root = window.Shopify?.routes?.root || '/';
                const productUrl = new URL(`${root}products/${encodeURIComponent(handle)}`, window.location.origin);
                productUrl.searchParams.set('view', this.cardView);

                const response = await fetch(productUrl.toString(), {
                    credentials: 'same-origin',
                    headers: {'X-Requested-With': 'fetch'},
                });

                if (!response.ok) {
                    throw new Error(`Failed to fetch product card for ${handle}. Status: ${response.status}`);
                }

                const text = await response.text();
                const parser = new DOMParser();
                const doc = parser.parseFromString(text, 'text/html');
                const cardRoot = doc.querySelector(this.cardSelector);

                if (!cardRoot) {
                    throw new Error(`Product card root ${this.cardSelector} not found for ${handle}`);
                }

                return cardRoot.innerHTML.trim();
            })().then((html) => {
                cache.set(cacheKey, Promise.resolve(html));
                return html;
            }).catch((error) => {
                cache.delete(cacheKey);
                console.error(LOG_PREFIX, 'fetchProductCardHTML error', handle, error);
                return '';
            });

            cache.set(cacheKey, pending);
            return pending;
        }

        buildSlide(html) {
            const slide = document.createElement('div');
            slide.className = 'swiper-slide sd:tw-shrink tb:tw-w-1/2';
            slide.innerHTML = html;
            return slide;
        }

        restoreFallbackSlides({clearIfMissing = false} = {}) {
            const fallbackMarkup = this.fallbackTemplate?.innerHTML?.trim() || '';

            if (fallbackMarkup) {
                this.swiperWrapper.innerHTML = fallbackMarkup;
            } else if (clearIfMissing) {
                this.swiperWrapper.innerHTML = '';
            }

            this.togglePagination(this.swiperWrapper.children.length);

            requestAnimationFrame(() => {
                this.swiperRoot?.refreshSwiper?.();
            });
        }

        togglePagination(slidesCount) {
            if (!this.swiperPagination) return;
            this.swiperPagination.classList.toggle('tw-hidden', Number(slidesCount) <= 1);
        }
    }

    customElements.define('po-header-menu-collection', PoHeaderMenuCollection);
})();
